import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestReset = async () => {
    try {
      await (await import("@/lib/api")).api.post("/auth/password-reset/request", { email: resetEmail });
      toast.success("If that email exists, we just sent you a reset link.");
    } catch (e) { toast.error("Failed"); }
  };

  const confirmReset = null; // handled on /reset page
  void confirmReset;

  return (
    <Layout>
      <section className="max-w-md mx-auto px-4 py-20">
        <Tabs defaultValue="login">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
            <TabsTrigger value="reset" data-testid="tab-reset">Reset password</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="mt-6">
            <h1 className="font-display text-3xl font-bold mb-2">Welcome back</h1>
            <p className="text-sm text-muted-foreground mb-6">Log in to your Creative Hub account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="pw">Password</Label>
                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
              </div>
              <Button type="submit" className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" disabled={loading} data-testid="login-submit">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
            </div>
            <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login-btn">Continue with Google</Button>
            <p className="mt-6 text-sm text-muted-foreground text-center">
              New here? <Link to="/signup" className="text-foreground font-medium">Create an account</Link>
            </p>
          </TabsContent>
          <TabsContent value="reset" className="mt-6">
            <h1 className="font-display text-3xl font-bold mb-2">Reset password</h1>
            <p className="text-sm text-muted-foreground mb-6">Enter your email — we&apos;ll send you a reset link.</p>
            <div className="space-y-3">
              <Input placeholder="Your email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} data-testid="reset-email" />
              <Button onClick={requestReset} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" data-testid="reset-request">Email me a reset link</Button>
              <p className="text-xs text-muted-foreground text-center">Already have a token? <Link to="/reset" className="underline">Enter it here</Link>.</p>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
